import { useState, type FormEvent } from "react";
import { useMeContext } from "../../MeApp";
import { useOnboardingForm } from "../useOnboardingForm";
import { useSaveContact } from "../../meHooks";
import { StepHeading, StepShell } from "@codel/dova-ds";
import { TextField } from "../../components/TextField";
import { Button } from "../components/Button";
import type { StepProps } from "../flowConfig";

/**
 * S3 — "A bit about you". Confirms the client's name AND collects their EMAIL. The
 * field is PRE-FILLED from whatever the therapist already set for this Person (the
 * acting partner's own PersonContacts, surfaced plain on `partner.email`), and is
 * always shown + editable.
 *
 * NO PHONE FIELD. It was here, optional, alongside the email. The number arrives on
 * its own at the END of this flow: S9 hands off to SMS, and the message the client
 * sends carries their number to us. Asking for it here was asking for something we
 * were about to be given, on the screen where a wary reader is deciding how much to
 * hand over.
 *
 * Edits persist through the existing `/me` contact endpoint (`useSaveContact`). The write
 * is AWAITED so a rejection (handle already in use → 409) blocks the advance and surfaces
 * inline — a typed value is never silently dropped. An unchanged pre-fill skips the write
 * entirely (the endpoint only ADDS channels, so re-sending the same handle is a redundant
 * no-op we avoid). The per-person invite code remains the SMS-identity mechanism; this is
 * supplementary reachability only.
 */
export function S3Identity({ onNext, onBack, currentStepIndex, totalSteps }: StepProps) {
  const { partner } = useMeContext();
  const { state, updateAnswers } = useOnboardingForm();
  // Falls back to "" when the partner hasn't loaded — guard below keeps a contact write
  // from ever targeting an empty (non-existent) person id.
  const partnerId = partner?.id ?? "";
  const saveContact = useSaveContact(partnerId);

  const fullName = [partner?.firstName, partner?.lastName]
    .filter(Boolean)
    .join(" ");
  const firstName = partner?.firstName ?? "";

  const [name, setName] = useState(state.answers.confirmedName ?? fullName);
  const [preferred, setPreferred] = useState(
    state.answers.preferredName ?? ""
  );
  const [nameError, setNameError] = useState("");

  // Pre-fill from the partner's existing contact (the acting partner sees their own
  // plain values). These are the baseline we diff against to skip a redundant write.
  const originalEmail = partner?.email ?? "";
  const [email, setEmail] = useState(originalEmail);
  const [contactError, setContactError] = useState("");


  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    // No real partner yet → a contact save would POST to id "" (a write against a
    // non-existent person). Stay put until the partner resolves.
    if (!partnerId) return;
    if (saveContact.isPending) return;
    if (!name.trim()) {
      setNameError("Enter your name");
      return;
    }
    setNameError("");

    const emailClean = email.trim();
    const contactChanged = emailClean !== originalEmail.trim();
    const emailIsValid = /^[^@\s]+@[^@\s]+$/.test(emailClean);

    // Only hit the contact endpoint when the client actually edited the address to a
    // valid entry. Await it so a duplicate handle blocks advancing (mirrors the old
    // standalone step's must-fix: never drop a typed value silently).
    //
    // `phone: null` still goes on the wire: the endpoint takes both channels, and
    // null is "no change to the phone" rather than "clear it". A number the
    // therapist already set for this person is not this screen's to erase.
    if (contactChanged) {
      if (!emailIsValid) {
        setContactError("Enter a valid email address");
        return;
      }
      setContactError("");
      try {
        await saveContact.mutateAsync({
          email: emailClean || null,
          phone: null,
        });
      } catch {
        setContactError(
          "That email didn't save. Check it and try again."
        );
        return;
      }
    }
    if (!contactChanged && !emailIsValid) {
      setContactError("Enter a valid email address");
      return;
    }

    const fallbackFirst = firstName || name.trim().split(/\s+/)[0];
    const finalPreferred = preferred.trim() || fallbackFirst;
    // Omit the key entirely when detection is falsy — an explicit `timezone: null`
    // would CLEAR a previously stored value, and this is a name/preferred-name save,
    // not a "clear my timezone" request.
    const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      await updateAnswers({
        confirmedName: name.trim(),
        preferredName: finalPreferred,
        ...(detectedTimezone ? { timezone: detectedTimezone } : {}),
      });
    } catch {
      setNameError(
        "Your details didn't save. Check each entry and use your legal first and last name."
      );
      return;
    }
    onNext();
  };

  return (
    <StepShell
      showBack
      onBack={onBack}
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      onSubmit={handleSubmit}
      footer={
        <Button type="submit" disabled={saveContact.isPending || !partnerId}>
          Continue
        </Button>
      }
    >
      <StepHeading title="A bit about you" />

      <div className="flex flex-col gap-6">
        <TextField
          id="confirmed-name"
          label="Confirm your full legal name"
          hint="Needed for health records and billing."
          value={name}
          onChange={(v) => {
            setName(v);
            if (nameError) setNameError("");
          }}
          error={nameError}
          placeholder="Full name"
          autoComplete="name"
          autoFocus
        />
        <TextField
          id="preferred-name"
          label="What should Dova call you?"
          value={preferred}
          onChange={setPreferred}
          placeholder={firstName || "A nickname or what you prefer"}
          autoComplete="nickname"
        />
        <TextField
          id="contact-email"
          label="Email"
          type="text"
          inputMode="email"
          value={email}
          onChange={(v) => {
            setEmail(v);
            if (contactError) setContactError("");
          }}
          placeholder="you@example.com"
          autoComplete="email"
        />
        {contactError && (
          <p role="alert" className="text-sm text-danger">
            {contactError}
          </p>
        )}
      </div>
    </StepShell>
  );
}
